import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { getUrl } from "../../helpers/getUrl";
import { setShowErrorModal } from "./preferenceSlice";
import { fetchStatus } from "./preferenceSlice";

const initialState = {
  mapping: {
    loading: false,
    defaultMappings: {
      order: [],
      customer: [],
      product: [],
      productFieldMappings: [],
    },
    error: "",
    currentRequestId: undefined,
  },
  isMappingSaved: {
    customer: true,
    order: true,
    product: true,
    productFieldMappings: true,
  },
  currentlyEditing: undefined,
  selectPreferenceHubspot: undefined,
  hubspot: {
    loading: false,
    hubspotDropdownFields: {},
    error: "",
    currentRequestId: undefined,
  },
  tripletex: {
    loading: false,
    tripletexDropdownFields: {},
    error: "",
    currentRequestId: undefined,
  },
  deletedHubspotFields: [],
  searchInput: "",
  searchFilteredArray: [],
  isEditMapping: false,
  isAllMappingsSelected: {
    customer: false,
    order: false,
    product: false,
    productFieldMappings: false,
  },
  filterSelected: {
    customer: [],
    order: [],
    product: [],
    productFieldMappings: [],
  },
  // pagination
  itemOffset: 0,
  slicedMappings: [],
  pageCount: 0,
  selectedIndex: 0,
};

export const fetchHsFields = createAsyncThunk(
  "mapping/fetchHsField",
  async (portalId, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_FETCH_HUBSPOT_FIELDS")}?portalId=${portalId}`
      );
      return response;
    } catch (error) {
      let errorMessage;
      if (error?.response && error.response?.data?.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
      throw error;
    }
  }
);

export const fetchTxFields = createAsyncThunk(
  "mapping/fetchTxFields",
  async (portalId, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_FETCH_TX_FIELDS")}?portalId=${portalId}`
      );
      return response;
    } catch (error) {
      let errorMessage;
      if (error?.response && error.response?.data?.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
      throw error;
    }
  }
);

const mappingSlice = createSlice({
  name: "mappingSlice",
  initialState,
  reducers: {
    /**
     * @description This function is used to update the page count
     */
    updatePageCount(state, { payload }) {
      state.pageCount = payload.count;
    },
    /**
     * @description This function is used to update itemOffset
     */
    updateItemOffset(state, { payload }) {
      state.itemOffset = payload.itemOffset;
    },
    /**
     * @description This function is used to update the selected index
     */
    updateSelectedIndex(state, { payload }) {
      state.selectedIndex = payload.selectedIndex;
    },
    /**
     * @description This function is used for updating custom mappings
     */
    updateSlicedMappings(state, { payload }) {
      state.slicedMappings = payload.mappings;
    },

    /**
     * @description This function is responsible for deleting existing mappings
     */
    onDeleteExistingMapping(state, { payload }) {
      const index = state.mapping.defaultMappings[
        payload.mappingsFor
      ].findIndex((mappedItem) => mappedItem.id === payload.id);

      if (payload.mappingsFor === "product") {
        state.mapping.defaultMappings[payload.mappingsFor][index] = {
          ...state.mapping.defaultMappings[payload.mappingsFor][index],
          HS_Fields: [],
        };
      } else {
        state.mapping.defaultMappings[payload.mappingsFor][index] = {
          ...state.mapping.defaultMappings[payload.mappingsFor][index],
          HS_FieldName_HRF: undefined,
          HS_FieldName_CRF: undefined,
          HS_FieldType: undefined,
          HS_Field_Description: undefined,
        };
      }
      state.isMappingSaved[payload.mappingsFor] = false;
    },
    onEditMapping(state, { payload }) {
      state.currentlyEditing = payload.id;
      state.isEditMapping = true;
    },
    /**
     * Reducers resets the state of preferences required for mapping when user clicks on cancel icon
     */
    onCancelMapping(state) {
      state.currentlyEditing = undefined;
      state.isEditMapping = false;
      state.selectPreferenceHubspot = undefined;
    },
    /**
     * Reducer updates the state of mappings when user clicks on check icon after selecting mappings
     */
    onSaveEditedMappings(state, { payload }) {
      if (state.selectPreferenceHubspot !== undefined) {
        const index = state.mapping.defaultMappings[
          payload.mappingsFor
        ].findIndex((mappedItem) => mappedItem.id === payload.id);

        state.mapping.defaultMappings[payload.mappingsFor][index] = {
          ...state.mapping.defaultMappings[payload.mappingsFor][index],
          HS_FieldName_CRF: state.selectPreferenceHubspot.CRF,
          HS_FieldName_HRF: state.selectPreferenceHubspot.HRF,
          HS_FieldType: state.selectPreferenceHubspot.type,
          HS_Field_Description: state.selectPreferenceHubspot.description ?? "",
        };
      }

      state.selectPreferenceHubspot = undefined;
      state.isEditMapping = false;
      state.currentlyEditing = undefined;
      state.isMappingSaved[payload.mappingsFor] = false;
    },
    /**
     * Reducer updates the state of mappings saved
     */
    checkIsMappingsSaved(state, { payload }) {
      state.isMappingSaved[payload.mappingsFor] = true;
    },
    /**
     * @description Returns the mappings based on the search input.
     */
    onSearchMappings(state, { payload }) {
      state.searchInput = payload.searchString;
      state.searchFilteredArray = [];
      if (state.searchInput !== "") {
        state.mapping.defaultMappings[payload.mappingsFor].forEach(
          (mappedItem, index) => {
            if (
              (mappedItem.HS_FieldName_HRF !== undefined &&
                mappedItem.HS_FieldName_HRF.toLowerCase().includes(
                  payload.searchString.toLowerCase()
                )) ||
              mappedItem.Tx_FieldName_HRF.toLowerCase().includes(
                payload.searchString.toLowerCase()
              )
            )
              state.searchFilteredArray.push(mappedItem.id);
          }
        );
      }
    },
    /**
     * @description Reducer updates the state of the hubspot dropdown value in the mapping window
     */
    setSelectHubspotPreference(state, { payload }) {
      state.selectPreferenceHubspot = payload.selectedOption;
    },
    /**
     * @description This function is responsible for validating HubSpot fields that are mapped.
     */
    validateHubspotProperties(state, { payload }) {
      state.deletedHubspotFields = [];
      const newSet = new Set(
        state.hubspot.hubspotDropdownFields[payload.mappingsFor].map(
          (hsProperty) => hsProperty.CRF
        )
      );

      if (payload.mappingsFor !== "product") {
        state.mapping.defaultMappings[payload.mappingsFor].forEach(
          (mapping) => {
            if (
              mapping.HS_FieldName_CRF !== undefined &&
              !newSet.has(mapping.HS_FieldName_CRF)
            ) {
              state.deletedHubspotFields.push(mapping.HS_FieldName_CRF);
            }
          }
        );
      } else {
        state.mapping.defaultMappings[payload.mappingsFor].forEach(
          (mapping) => {
            if (mapping.HS_Fields && mapping.HS_Fields.length > 0) {
              mapping.HS_Fields.forEach((hsField) => {
                if (!newSet.has(hsField.HS_FieldName_CRF)) {
                  state.deletedHubspotFields.push(hsField.HS_FieldName_CRF);
                }
              });
            }
          }
        );
      }
    },
    /**
     * @description Reducer updates the state of mappings
     */
    checkUnMappedTripletexFields(state, { payload }) {
      const newSet = new Set(
        state.mapping.defaultMappings[payload.mappingsFor].map(
          (mappedItem) => mappedItem.Tx_FieldName_CRF
        )
      );

      state.tripletex.tripletexDropdownFields[payload.mappingsFor].forEach(
        (txProperty) => {
          if (!newSet.has(txProperty.CRF)) {
            let newMapping = {};
            if (payload.mappingsFor !== "product") {
              newMapping = {
                HS_FieldName_HRF: undefined,
                Tx_FieldName_HRF: txProperty.HRF,
                HS_FieldName_CRF: undefined,
                Tx_FieldName_CRF: txProperty.CRF,
                HS_FieldType: undefined,
                Tx_FieldType: txProperty.type,
                HS_Field_Description: undefined,
                Tx_Field_Description: txProperty.description ?? "",
                isChecked: false,
                id: `${txProperty.CRF}-${payload.mappingsFor}`,
              };
            } else {
              newMapping = {
                Tx_FieldName_HRF: txProperty.HRF,
                Tx_FieldName_CRF: txProperty.CRF,
                Tx_FieldType: txProperty.type,
                Tx_Field_Description: txProperty.description ?? "",
                HS_Fields: [],
                isChecked: false,
                id: `${txProperty.CRF}-${payload.mappingsFor}`,
              };
            }
            if (txProperty.required) {
              newMapping.Tx_Field_Required = txProperty.required;
            }
            state.mapping.defaultMappings[payload.mappingsFor].push(newMapping);
          }
        }
      );
    },
    /**
     * @description Reducer updates the state of mappings when user clicked on checkbox for selecting all the mappings
     */
    onSelectAllMappings(state, { payload }) {
      if (payload.isChecked) {
        state.isAllMappingsSelected[payload.mappingsFor] = true;
        state.mapping.defaultMappings[payload.mappingsFor].forEach(
          (mapping) => {
            if (
              (payload.mappingsFor !== "product" &&
                !mapping.Tx_Field_Required &&
                mapping.HS_FieldName_CRF !== undefined) ||
              (payload.mappingsFor === "product" &&
                mapping.HS_Fields.length !== 0)
            )
              mapping.isChecked = true;
          }
        );
      } else if (!payload.isChecked) {
        state.isAllMappingsSelected[payload.mappingsFor] = false;
        state.mapping.defaultMappings[payload.mappingsFor].forEach(
          (mapping) => (mapping.isChecked = false)
        );
      }
    },
    /**
     * @description Reducer updates the state of checked mapping when user clicked on checkbox
     */
    onSelectMapping(state, { payload }) {
      state.isAllMappingsSelected[payload.mappingsFor] = false;
      if (payload.isChecked) {
        // Find index of the selected mapping
        const index = state.mapping.defaultMappings[
          payload.mappingsFor
        ].findIndex((mapping) => mapping.Tx_FieldName_CRF === payload.txValue);

        state.mapping.defaultMappings[payload.mappingsFor][
          index
        ].isChecked = true;
      } else if (!payload.isChecked) {
        // Find index of the selected mapping
        const index = state.mapping.defaultMappings[
          payload.mappingsFor
        ].findIndex((mapping) => mapping.Tx_FieldName_CRF === payload.txValue);

        state.mapping.defaultMappings[payload.mappingsFor][
          index
        ].isChecked = false;
      }
    },
    /**
     * @description Reducer updates the state of checked mappings
     */
    onFilterSelectedMappings(state, { payload }) {
      if (state.mapping.defaultMappings[payload.mappingsFor]) {
        const filtered = state.mapping.defaultMappings[
          payload.mappingsFor
        ].filter((mappedItem) => mappedItem.isChecked === true);

        state.filterSelected[payload.mappingsFor] = filtered;
      }
    },
    /**
     * @description This reducer is used for deleting multiple mappings with a single click
     */
    onDeleteSelectedMappings(state, { payload }) {
      state.mapping.defaultMappings[payload.mappingsFor].forEach(
        (mapping, index) => {
          if (mapping.isChecked) {
            if (payload.mappingsFor !== "product") {
              state.mapping.defaultMappings[payload.mappingsFor][index] = {
                ...mapping,
                HS_FieldName_HRF: undefined,
                HS_FieldName_CRF: undefined,
                HS_FieldType: undefined,
                HS_Field_Description: undefined,
                isChecked: false,
              };
            } else {
              state.mapping.defaultMappings[payload.mappingsFor][index] = {
                ...mapping,
                HS_Fields: [],
                isChecked: false,
              };
            }
          }
        }
      );
      state.isMappingSaved[payload.mappingsFor] = false;
      state.filterSelected[payload.mappingsFor] = [];
      state.isAllMappingsSelected[payload.mappingsFor] = false;
    },
    /**
     * @description This reducer is used for adding or removing HS fields to custom product mappings
     */
    onAddHsFieldsToCustomMappings(state, { payload }) {
      const { checked, mappingsFor, mappedItem, selectedValue } = payload;

      const index = state.mapping.defaultMappings[mappingsFor].findIndex(
        (mapped) => mapped.id === mappedItem.id
      );

      if (index !== -1) {
        if (checked === true) {
          state.mapping.defaultMappings[mappingsFor][index].HS_Fields.push({
            HS_FieldName_HRF: selectedValue.HRF,
            HS_FieldName_CRF: selectedValue.CRF,
            HS_FieldType: selectedValue.type,
            HS_Field_Description: selectedValue.description,
          });
        } else {
          const hsIndex = state.mapping.defaultMappings[mappingsFor][
            index
          ].HS_Fields.findIndex(
            (hsField) => hsField.HS_FieldName_CRF === selectedValue.CRF
          );

          if (hsIndex !== -1) {
            state.mapping.defaultMappings[mappingsFor][index].HS_Fields.splice(
              hsIndex,
              1
            );
          }
        }

        state.isMappingSaved.product = false;
      }
    },
    clearDefaultValue(state) {
      state.searchInput = "";
      state.searchFilteredArray = [];
      state.currentlyEditing = undefined;
      state.isEditMapping = false;
      state.selectPreferenceHubspot = undefined;
      state.selectedIndex = 0;
      state.itemOffset = 0;
      state.pageCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStatus.fulfilled, (state, actions) => {
      const { mapping } = actions.payload;

      const dummyObject = {
        customer: [],
        order: [],
        product: [],
        productFieldMappings: [],
      };
      Object.keys(mapping).forEach((category) =>
        mapping[category].forEach((eachMapping) => {
          eachMapping = {
            ...eachMapping,
            isChecked: false,
            id: `${eachMapping.Tx_FieldName_CRF}-${category}`,
          };
          dummyObject[category].push(eachMapping);
        })
      );
      state.mapping.defaultMappings = dummyObject;
    });

    builder.addCase(fetchHsFields.pending, (state, action) => {
      if (!state.hubspot.loading) {
        state.hubspot.loading = true;
        state.hubspot.currentRequestId = action.meta.requestId;
      }
    });
    builder.addCase(fetchHsFields.fulfilled, (state, action) => {
      if (
        state.hubspot.loading &&
        state.hubspot.currentRequestId === action.meta.requestId
      ) {
        state.hubspot.loading = false;
        state.hubspot.hubspotDropdownFields = action.payload;
        state.hubspot.error = "";
        state.hubspot.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchHsFields.rejected, (state, action) => {
      if (
        state.hubspot.loading &&
        state.hubspot.currentRequestId === action.meta.requestId
      ) {
        state.hubspot.loading = false;
        state.hubspot.hubspotDropdownFields = [];
        state.hubspot.error = action.error.message ?? "";
        state.hubspot.currentRequestId = undefined;
      }
    });

    builder.addCase(fetchTxFields.pending, (state, action) => {
      if (!state.tripletex.loading) {
        state.tripletex.loading = true;
        state.tripletex.currentRequestId = action.meta.requestId;
      }
    });
    builder.addCase(fetchTxFields.fulfilled, (state, action) => {
      if (
        state.tripletex.loading &&
        state.tripletex.currentRequestId === action.meta.requestId
      ) {
        state.tripletex.loading = false;
        state.tripletex.tripletexDropdownFields = action.payload;
        state.tripletex.error = "";
        state.tripletex.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchTxFields.rejected, (state, action) => {
      if (
        state.tripletex.loading &&
        state.tripletex.currentRequestId === action.meta.requestId
      ) {
        state.tripletex.loading = false;
        state.tripletex.tripletexDropdownFields = [];
        state.tripletex.error = action.error.message ?? "";
        state.tripletex.currentRequestId = undefined;
      }
    });
  },
});

export const {
  onDeleteExistingMapping,
  onDeleteSelectedMappings,
  onSearchMappings,
  updatePageCount,
  updateItemOffset,
  updateSelectedIndex,
  updateSlicedMappings,
  onEditMapping,
  onCancelMapping,
  onSaveEditedMappings,
  checkIsMappingsSaved,
  setSelectHubspotPreference,
  validateHubspotProperties,
  checkUnMappedTripletexFields,
  onSelectAllMappings,
  onSelectMapping,
  onFilterSelectedMappings,
  onAddHsFieldsToCustomMappings,
  clearDefaultValue,
} = mappingSlice.actions;

export default mappingSlice.reducer;
